import { ViewSwitch } from '../ui/ListToolbar';
import { FaCalendarDay, FaCalendarDays, FaCalendarWeek, FaList } from '../ui/FaIcons';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { calendarPreview, pillLines, type CalendarEvent } from '../../services/calendar/calendarAggregate';
import {
  CALENDAR_FOCUS_START_MIN,
  DAY_END_MIN,
  DAY_START_MIN,
  WEEK_END_MIN,
  addDaysYmd,
  clockOf,
  daysForView,
  formatHm,
  parseLocalYmd,
  placeEvents,
  startOfWeekMonday,
  toneForDossier,
  type AgendaTone,
} from '../../services/calendar/agendaLayout';
import type { CalendarView } from '../../services/calendar/calendarUrlState';
import { fcDateToYmd } from '../../services/calendar/calendarUrlState';
import { eventTypeColor, tintOf, type AgendaColorMode, type ColorMap } from '../../services/calendar/eventColors';
import './agenda.css';

const PX = 52;
const WEEK_PX = 48;

function useFocusOnNine(ref: RefObject<HTMLDivElement | null>, key: string) {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const hour = root.querySelector(`[data-minute="${CALENDAR_FOCUS_START_MIN}"]`);
    if (!(hour instanceof HTMLElement)) return;
    const head = root.querySelector('.ag-wh');
    const headH = head instanceof HTMLElement ? head.getBoundingClientRect().height : 0;
    const top = hour.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
    root.scrollTop = Math.max(0, top - headH);
  }, [key, ref]);
}

/** Clic sur un créneau vide : jour, heure (absente en vue mois) et point d'ancrage à l'écran. */
export interface AgendaSlot {
  day: string;
  time: string | null;
  x: number;
  y: number;
}

const SlotContext = createContext<((slot: AgendaSlot) => void) | undefined>(undefined);

/** Couleur d'un bloc : celle de son type, ou la teinte de son dossier selon le réglage de l'agenda. */
const PaintContext = createContext<{ mode: AgendaColorMode; colors: ColorMap }>({ mode: 'dossier', colors: {} });

function usePaint(event: CalendarEvent): { className: string; style?: CSSProperties } {
  const { mode, colors } = useContext(PaintContext);
  const color = eventTypeColor(event, mode, colors);
  if (color) {
    return { className: 'ag-typed', style: { background: tintOf(color), ['--ag-accent' as string]: color } };
  }
  const tone: AgendaTone = event.kind === 'prescription' ? 'ink' : toneForDossier(event.dossierId);
  return { className: `ag-${tone}` };
}

function EventDot({ event }: { event: CalendarEvent }) {
  const paint = usePaint(event);
  // La pastille ne prend que la couleur d'accent, pas le fond clair du bloc.
  const { background: _tint, ...accent } = paint.style ?? {};
  return <i className={`ag-dot ${paint.className}`} style={accent} />;
}

/** Heure du créneau cliqué, à la demi-heure, d'après la position dans la colonne. */
function slotTime(event: MouseEvent<HTMLElement>, startMin: number, pxPerHour: number): string {
  const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
  const minutes = Math.max(0, Math.min(23 * 60 + 30, startMin + Math.floor((y / pxPerHour) * 2) * 30));
  return formatHm(minutes);
}

const VIEW_LABEL: Record<string, string> = {
  day: 'Journée',
  week: 'Semaine',
  month: 'Mois',
  planning: 'Vue Planning',
  list: 'Vue Planning',
};

interface Props {
  view: CalendarView;
  date: string;
  today: string;
  events: CalendarEvent[];
  lawyerNames: Record<string, string>;
  query: string;
  onQuery: (value: string) => void;
  onView: (view: CalendarView) => void;
  /** Clic sur un créneau vide (jour, semaine) ou sur une case du mois. Absent : pas de création rapide. */
  onSlot?: (slot: AgendaSlot) => void;
  onDate: (ymd: string) => void;
  onShift: (dir: -1 | 1) => void;
  onToday: () => void;
  onEvent: (event: CalendarEvent) => void;
  /** Absent : teinte par dossier, comme avant les types d'événement. */
  colorMode?: AgendaColorMode;
  colors?: ColorMap;
}

function shown(view: CalendarView): CalendarView {
  return view === 'list' ? 'planning' : view;
}

type PreviewHandlers = {
  onPreview: (event: CalendarEvent, el: HTMLElement) => void;
  onPreviewEnd: () => void;
};

export function AgendaBoard(props: Props) {
  const view = shown(props.view);
  const rootRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ event: CalendarEvent; el: HTMLElement } | null>(null);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  const [previewOn, setPreviewOn] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewToken = useRef(0);
  const previewShown = useRef(false);
  const queuedPreview = useRef<{ event: CalendarEvent; el: HTMLElement } | null>(null);

  const clearHide = () => {
    if (hideTimer.current != null) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const revealPreview = (token: number) => {
    const next = queuedPreview.current;
    queuedPreview.current = null;
    if (!next || previewToken.current !== token) return;
    setHover(next);
    setHoverRect(next.el.getBoundingClientRect());
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (previewToken.current !== token) return;
        previewShown.current = true;
        setPreviewOn(true);
      });
    });
  };

  const fadePreviewOut = (token: number) => {
    previewShown.current = false;
    setPreviewOn(false);
    clearHide();
    hideTimer.current = setTimeout(() => {
      if (previewToken.current !== token) return;
      if (queuedPreview.current) {
        revealPreview(token);
        return;
      }
      setHover(null);
      setHoverRect(null);
      hideTimer.current = null;
    }, 90);
  };

  useEffect(() => () => clearHide(), []);

  const preview: PreviewHandlers = {
    onPreview: (event, el) => {
      if (previewShown.current && hover?.event.id === event.id) {
        setHover({ event, el });
        setHoverRect(el.getBoundingClientRect());
        return;
      }
      queuedPreview.current = { event, el };
      if (previewShown.current) {
        fadePreviewOut(++previewToken.current);
        return;
      }
      if (hover) return;
      revealPreview(++previewToken.current);
    },
    onPreviewEnd: () => {
      queuedPreview.current = null;
      if (!previewShown.current && !hover) return;
      fadePreviewOut(++previewToken.current);
    },
  };

  useEffect(() => {
    if (!hover) return;
    const sync = () => setHoverRect(hover.el.getBoundingClientRect());
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [hover]);
  const q = props.query.trim().toLowerCase();
  const events = props.events.filter((e) => {
    if (!q) return true;
    const hay = `${e.dossierTitle ?? ''} ${e.description} ${e.title}`.toLowerCase();
    return hay.includes(q);
  });
  const tasks = events.filter((e) => e.kind === 'task');
  const paint = useMemo(
    () => ({ mode: props.colorMode ?? 'dossier', colors: props.colors ?? {} }),
    [props.colorMode, props.colors],
  );

  return (
    <SlotContext.Provider value={props.onSlot}>
    <PaintContext.Provider value={paint}>
    <div className="cime-agenda" ref={rootRef}>
      <div className="ag-toolbar">
        <label className="ag-search">
          <svg viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden>
            <path d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z" />
          </svg>
          <input
            value={props.query}
            onChange={(e) => props.onQuery(e.target.value)}
            placeholder="Rechercher par objet…"
            aria-label="Rechercher dans l’agenda"
          />
        </label>
        <div className="ag-nav">
          <button type="button" className="ag-today" onClick={props.onToday}>Aujourd’hui</button>
          <button type="button" className="ag-icon" aria-label="Précédent" onClick={() => props.onShift(-1)}>←</button>
          <div className="ag-date">{labelFor(view, props.date)}</div>
          <button type="button" className="ag-icon" aria-label="Suivant" onClick={() => props.onShift(1)}>→</button>
        </div>
        <ViewSwitch
          label="Vue du calendrier"
          value={view}
          onChange={props.onView}
          options={[
            { value: 'day', label: VIEW_LABEL.day ?? 'Jour', icon: <FaCalendarDay aria-hidden /> },
            { value: 'week', label: VIEW_LABEL.week ?? 'Semaine', icon: <FaCalendarWeek aria-hidden /> },
            { value: 'month', label: VIEW_LABEL.month ?? 'Mois', icon: <FaCalendarDays aria-hidden /> },
            { value: 'planning', label: VIEW_LABEL.planning ?? 'Planning', icon: <FaList aria-hidden /> },
          ]}
        />
      </div>
      <div className={view === 'day' || view === 'week' ? 'ag-board' : 'ag-scroll'}>
        {view === 'day' && <DayColumn date={props.date} today={props.today} events={events} onEvent={props.onEvent} {...preview} />}
        {view === 'week' && (
          <WeekColumns view={view} date={props.date} today={props.today} events={events} onEvent={props.onEvent} {...preview} />
        )}
        {view === 'month' && <MonthGrid date={props.date} today={props.today} events={events} onEvent={props.onEvent} onDate={props.onDate} {...preview} />}
        {view === 'planning' && (
          <PlanningTable date={props.date} events={events} lawyerNames={props.lawyerNames} onEvent={props.onEvent} {...preview} />
        )}
      </div>
      <details className="ag-drawer">
        <summary>Tâches et échéances <span className="ag-count">{tasks.length}</span></summary>
        {tasks.map((t) => (
          <button key={t.id} type="button" className="ag-task" onClick={() => props.onEvent(t)}>
            <span>{whenLabel(t)}</span>
            <span>{t.title}</span>
          </button>
        ))}
      </details>
      {hover && hoverRect && rootRef.current && createPortal(
        <PreviewCard key={hover.event.id} event={hover.event} rect={hoverRect} shown={previewOn} lawyerName={hover.event.lawyerId ? props.lawyerNames[hover.event.lawyerId] ?? null : null} />,
        rootRef.current,
      )}
    </div>
    </PaintContext.Provider>
    </SlotContext.Provider>
  );
}

function labelFor(view: CalendarView, ymd: string): string {
  const d = parseLocalYmd(ymd);
  if (view === 'day') {
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (view === 'month' || view === 'planning') {
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }
  const days = daysForView(view, ymd);
  const a = parseLocalYmd(days[0] ?? ymd);
  const b = parseLocalYmd(days[days.length - 1] ?? ymd);
  const left = a.toLocaleDateString('fr-FR', { day: 'numeric', month: a.getMonth() === b.getMonth() ? undefined : 'short' });
  const right = b.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${left} – ${right}`;
}

function objectLabel(title: string): string {
  const i = title.indexOf(' — ');
  return i >= 0 ? title.slice(0, i) : title;
}

function dossierLabel(title: string): string {
  const i = title.indexOf(' — ');
  return i >= 0 ? title.slice(i + 3) : '—';
}

function whenLabel(event: CalendarEvent): string {
  const clock = clockOf(event.start, event.allDay, event.end);
  const d = parseLocalYmd(clock.ymd);
  const day = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  if (clock.startMin == null) return day;
  if (!event.end || clock.endMin == null) return `${day} ${formatHm(clock.startMin)}`;
  return `${day} ${formatHm(clock.startMin)}–${formatHm(clock.endMin)}`;
}

function onDay(events: CalendarEvent[], ymd: string) {
  return events
    .map((event) => ({ event, clock: clockOf(event.start, event.allDay, event.end) }))
    .filter((row) => row.clock.ymd === ymd);
}

function DayColumn({ date, events, onEvent, onPreview, onPreviewEnd }: { date: string; today: string; events: CalendarEvent[]; onEvent: (e: CalendarEvent) => void } & PreviewHandlers) {
  const onSlot = useContext(SlotContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  useFocusOnNine(scrollRef, date);
  const rows = onDay(events, date);
  const allDay = rows.filter((r) => r.clock.allDay);
  const timed = rows.filter((r) => r.clock.startMin != null);
  const placed = placeEvents(timed.map((r) => ({
    id: r.event.id,
    startMin: Math.max(r.clock.startMin ?? DAY_START_MIN, DAY_START_MIN),
    endMin: Math.min(r.clock.endMin ?? DAY_START_MIN + 60, DAY_END_MIN),
  }))).filter((p) => p.endMin > p.startMin);
  const byId = new Map(timed.map((r) => [r.event.id, r.event]));
  const hours = [];
  for (let m = DAY_START_MIN; m < DAY_END_MIN; m += 60) hours.push(m);
  return (
    <>
      {allDay.length > 0 && (
        <div className="ag-allday">
          {allDay.map((r) => (
            <EventButton key={r.event.id} event={r.event} className="ag-chip" onEvent={onEvent} onPreview={onPreview} onPreviewEnd={onPreviewEnd} label={pillText(r.event)} />
          ))}
        </div>
      )}
      <div className="ag-time" ref={scrollRef}>
      <div className="ag-day">
        <div>
          {hours.map((m) => <div key={m} className="ag-hour" data-minute={m}>{formatHm(m)}</div>)}
        </div>
        <div
          className={onSlot ? 'ag-lane ag-can-add' : 'ag-lane'}
          onClick={(e) => {
            if (!onSlot || (e.target as HTMLElement).closest('button')) return;
            onSlot({ day: date, time: slotTime(e, DAY_START_MIN, PX), x: e.clientX, y: e.clientY });
          }}
        >
          <div>{hours.map((m) => <div key={m} className="ag-slot" />)}</div>
          <div className="ag-events">
            {placed.map((p) => {
              const event = byId.get(p.id);
              if (!event) return null;
              const top = ((p.startMin - DAY_START_MIN) / 60) * PX;
              const height = Math.max(((p.endMin - p.startMin) / 60) * PX - 3, 22);
              const clock = clockOf(event.start, false);
              const time = formatHm(clock.startMin ?? p.startMin).replace(':00', 'h').replace(':', 'h');
              return (
                <EventButton
                  key={p.id}
                  event={event}
                  className="ag-ev"
                  onEvent={onEvent}
                  onPreview={onPreview}
                  onPreviewEnd={onPreviewEnd}
                  label={pillText(event, time)}
                  style={{
                    top,
                    height,
                    left: `calc(${(100 / p.cols) * p.col}% + 3px)`,
                    width: `calc(${100 / p.cols}% - 6px)`,
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

function WeekColumns({ view, date, today, events, onEvent, onPreview, onPreviewEnd }: { view: CalendarView; date: string; today: string; events: CalendarEvent[]; onEvent: (e: CalendarEvent) => void } & PreviewHandlers) {
  const onSlot = useContext(SlotContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  useFocusOnNine(scrollRef, `${view}:${date}`);
  const days = daysForView(view, date);
  const from = DAY_START_MIN;
  const to = WEEK_END_MIN;
  const hours = [];
  for (let m = from; m < to; m += 60) hours.push(m);
  return (
    <div className="ag-time ag-time-week" ref={scrollRef}>
    <div className="ag-week" style={{ ['--ag-cols' as string]: days.length }}>
      <div />
      {days.map((ymd) => {
        const d = parseLocalYmd(ymd);
        return (
          <div key={ymd} className={ymd === today ? 'ag-wh is-today' : 'ag-wh'}>
            <div className="ag-dow">{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
            <div className="ag-num">{d.getDate()}</div>
          </div>
        );
      })}
      <div>
        {hours.map((m) => <div key={m} className="ag-hour" data-minute={m} style={{ height: WEEK_PX }}>{formatHm(m)}</div>)}
      </div>
      {days.map((ymd) => {
        const rows = onDay(events, ymd).filter((r) => r.clock.startMin != null);
        const placed = placeEvents(rows.map((r) => ({
          id: r.event.id,
          startMin: r.clock.startMin ?? from,
          endMin: r.clock.endMin ?? from + 60,
        }))).filter((p) => p.endMin > from && p.startMin < to);
        const byId = new Map(rows.map((r) => [r.event.id, r.event]));
        return (
          <div
            key={ymd}
            className={onSlot ? 'ag-wd ag-can-add' : 'ag-wd'}
            style={{ height: hours.length * WEEK_PX }}
            onClick={(e) => {
              if (!onSlot || (e.target as HTMLElement).closest('button')) return;
              onSlot({ day: ymd, time: slotTime(e, from, WEEK_PX), x: e.clientX, y: e.clientY });
            }}
          >
            {placed.map((p) => {
              const event = byId.get(p.id);
              if (!event) return null;
              const top = ((Math.max(p.startMin, from) - from) / 60) * WEEK_PX;
              const height = Math.max(((Math.min(p.endMin, to) - Math.max(p.startMin, from)) / 60) * WEEK_PX - 4, 34);
              return (
                <EventButton
                  key={p.id}
                  event={event}
                  className="ag-pill"
                  onEvent={onEvent}
                  onPreview={onPreview}
                  onPreviewEnd={onPreviewEnd}
                  label={pillText(event)}
                  style={{
                    top: top + 2,
                    height,
                    left: `calc(${(100 / p.cols) * p.col}% + 3px)`,
                    width: `calc(${100 / p.cols}% - 6px)`,
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
    </div>
  );
}

function MonthGrid({ date, today, events, onEvent, onDate, onPreview, onPreviewEnd }: { date: string; today: string; events: CalendarEvent[]; onEvent: (e: CalendarEvent) => void; onDate: (ymd: string) => void } & PreviewHandlers) {
  const onSlot = useContext(SlotContext);
  const cursor = parseLocalYmd(date);
  const month = cursor.getMonth();
  const start = startOfWeekMonday(fcDateToYmd(new Date(cursor.getFullYear(), month, 1, 12)));
  const names = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const cells = Array.from({ length: 42 }, (_, i) => addDaysYmd(start, i));
  return (
    <div className="ag-month">
      {names.map((n) => <div key={n} className="ag-mh">{n}</div>)}
      {cells.map((ymd) => {
        const d = parseLocalYmd(ymd);
        const rows = onDay(events, ymd);
        const extra = rows.length > 3 ? rows.length - 3 : 0;
        return (
          <div
            key={ymd}
            className={`ag-md${d.getMonth() !== month ? ' is-out' : ''}${ymd === today ? ' is-today' : ''}${onSlot ? ' ag-can-add' : ''}`}
            onClick={(e) => {
              if (!onSlot || (e.target as HTMLElement).closest('button')) return;
              onSlot({ day: ymd, time: null, x: e.clientX, y: e.clientY });
            }}
          >
            <button type="button" className="ag-num" onClick={() => onDate(ymd)}>
              {d.getDate()}
            </button>
            {rows.slice(0, 3).map((r) => (
              <EventButton
                key={r.event.id}
                event={r.event}
                className="ag-chip"
                onEvent={onEvent}
                onPreview={onPreview}
                onPreviewEnd={onPreviewEnd}
                label={pillText(r.event, r.clock.startMin != null ? formatHm(r.clock.startMin) : undefined)}
              />
            ))}
            {extra > 0 && <div className="ag-dow">+ {extra}</div>}
          </div>
        );
      })}
    </div>
  );
}

function PlanningTable({ date, events, lawyerNames, onEvent, onPreview, onPreviewEnd }: { date: string; events: CalendarEvent[]; lawyerNames: Record<string, string>; onEvent: (e: CalendarEvent) => void } & PreviewHandlers) {
  const month = parseLocalYmd(date).getMonth();
  const year = parseLocalYmd(date).getFullYear();
  const rows = events.filter((e) => {
    const c = clockOf(e.start, e.allDay);
    const d = parseLocalYmd(c.ymd);
    return d.getMonth() === month && d.getFullYear() === year;
  });
  return (
    <table className="ag-plan">
      <thead>
        <tr>
          <th>Date et heure</th>
          <th>Objet</th>
          <th>Responsable</th>
          <th>Dossier</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((event) => {
          const who = (event.lawyerId && lawyerNames[event.lawyerId]) || '—';
          return (
            <tr
              key={event.id}
              onClick={() => onEvent(event)}
              onMouseEnter={(e) => onPreview(event, e.currentTarget)}
              onMouseLeave={onPreviewEnd}
            >
              <td>{whenLabel(event)}</td>
              <td><EventDot event={event} />{objectLabel(event.title)}</td>
              <td>{who}</td>
              <td>{dossierLabel(event.title)}</td>
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr><td colSpan={4}>Aucune échéance ce mois-ci</td></tr>
        )}
      </tbody>
    </table>
  );
}

function pillText(event: CalendarEvent, time?: string): ReactNode {
  const lines = pillLines(event);
  return (
    <span className="ag-pill-lines">
      {lines.map((line, i) => (
        <span key={i} className="ag-pill-line">
          {i === 0 && time ? <><b>{time}</b> {line}</> : line}
        </span>
      ))}
    </span>
  );
}

function previewStyle(rect: DOMRect): CSSProperties {
  const margin = 8;
  const width = 280;
  const below = rect.bottom + margin;
  const flip = window.innerHeight - below < 150 && rect.top > 150;
  const maxLeft = window.innerWidth - Math.min(width, window.innerWidth - 16) - margin;
  const left = Math.max(margin, Math.min(rect.left, maxLeft));
  return {
    left,
    top: flip ? undefined : below,
    bottom: flip ? window.innerHeight - rect.top + margin : undefined,
  };
}

function PreviewCard({ event, rect, shown, lawyerName }: { event: CalendarEvent; rect: DOMRect; shown: boolean; lawyerName: string | null }) {
  const card = calendarPreview(event, whenLabel(event), lawyerName);
  return (
    <div className={shown ? 'ag-preview is-on' : 'ag-preview'} style={previewStyle(rect)} role="tooltip">
      <div className="ag-preview-type">{card.typeLabel}</div>
      {card.dossierTitle && <div className="ag-preview-dossier">{card.dossierTitle}</div>}
      <div className="ag-preview-desc">{card.description}</div>
      <div className="ag-preview-meta">{card.when}</div>
      {card.responsable && <div className="ag-preview-meta">{card.responsable}</div>}
    </div>
  );
}

function EventButton({
  event, className, label, style, onEvent, onPreview, onPreviewEnd,
}: {
  event: CalendarEvent;
  className: string;
  label: ReactNode;
  style?: CSSProperties;
  onEvent: (e: CalendarEvent) => void;
} & PreviewHandlers) {
  const paint = usePaint(event);
  return (
    <button
      type="button"
      className={`${className} ${paint.className}`}
      style={paint.style ? { ...style, ...paint.style } : style}
      onClick={() => onEvent(event)}
      onMouseEnter={(e) => onPreview(event, e.currentTarget)}
      onMouseLeave={onPreviewEnd}
    >
      {label}
    </button>
  );
}
